import React from "react";
import { Modal, Table } from "react-bootstrap";
import { Skeleton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Button } from "../../common/Button";
import {
  formatAuditTimestamp,
  getDeliveryStatusChip,
  truncateError,
} from "./Constants";

const SUMMARY_COLUMNS = [
  "Registry",
  "Delivery type",
  "File type",
  "Latest status",
  "Manual retries",
  "Delivery attempts",
  "Last error",
  "Last completed",
  "Actions",
];

const isRetryEligible = (row) => row?.latestStatus === "FAILED";

function LatestStatusChip({ latestStatus }) {
  const chip = getDeliveryStatusChip({ status: latestStatus });
  return (
    <span className={chip.className} title={chip.title}>
      {chip.label}
    </span>
  );
}

function EncounterDetailModal({
  show,
  orgId,
  orgName,
  encounterId,
  summaryByRegistry,
  loading,
  retrySubmitting,
  onClose,
  onRetryRegistry,
  onRetryAll,
}) {
  const navigate = useNavigate();
  const hasFailedRows = summaryByRegistry?.some(isRetryEligible);
  const displayOrg = orgName || orgId || "—";

  const handleClose = () => {
    if (retrySubmitting) return;
    onClose?.();
  };

  const openEncounter = () => {
    if (retrySubmitting) return;
    navigate("/app/encounter-details", {
      state: { encounterId, notPatientDashboard: true },
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={retrySubmitting ? "static" : true}
      keyboard={!retrySubmitting}
      centered
      dialogClassName="rda-detail-modal-dialog"
      className="rda-detail-modal"
      aria-labelledby="rda-detail-modal-title"
    >
      <Modal.Header closeButton={!retrySubmitting} className="border-0">
        <Modal.Title id="rda-detail-modal-title">
          Report Delivery Summary
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="rda-detail__meta">
          <span>
            <strong>Org:</strong> {displayOrg}
          </span>
          <span>
            <strong>Encounter:</strong>{" "}
            {encounterId ? (
              <button
                type="button"
                className="rda-link-btn"
                onClick={openEncounter}
                disabled={retrySubmitting}
              >
                {encounterId}
              </button>
            ) : (
              "—"
            )}
          </span>
        </div>

        <div className="rda-table-wrap table-wrap">
          <Table responsive className="rda-table">
            <thead>
              <tr>
                {SUMMARY_COLUMNS.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? [1, 2, 3].map((n) => (
                    <tr key={`sum-sk-${n}`}>
                      {SUMMARY_COLUMNS.map((col) => (
                        <td key={col}>
                          <Skeleton animation="wave" />
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
              {!loading && summaryByRegistry?.length > 0
                ? summaryByRegistry.map((row) => {
                    const err = row.lastError || "";
                    const truncated = truncateError(err);
                    const key = `${row.registryId || ""}#${row.fileType || ""}`;
                    return (
                      <tr key={key}>
                        <td>{row.registryName || row.registryId || "—"}</td>
                        <td>{row.deliveryType || "—"}</td>
                        <td>{row.fileType || "—"}</td>
                        <td>
                          <LatestStatusChip latestStatus={row.latestStatus} />
                        </td>
                        <td>{row.manualRetryCount ?? "—"}</td>
                        <td>{row.deliveryAttemptCount ?? "—"}</td>
                        <td>
                          {err ? (
                            truncated !== err ? (
                              <Tooltip title={err} arrow>
                                <span className="rda-error-cell">{truncated}</span>
                              </Tooltip>
                            ) : (
                              <span className="rda-error-cell">{truncated}</span>
                            )
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{formatAuditTimestamp(row.lastCompletedAt)}</td>
                        <td>
                          {isRetryEligible(row) ? (
                            <Button
                              type="button"
                              variant="secondary"
                              className="rda-retry-btn"
                              disabled={retrySubmitting || loading}
                              onClick={() => onRetryRegistry?.(row)}
                            >
                              Retry
                            </Button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })
                : null}
              {!loading &&
              (!summaryByRegistry || summaryByRegistry.length === 0) ? (
                <tr>
                  <td colSpan={SUMMARY_COLUMNS.length} className="rda-empty">
                    No registry summary for this encounter
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        {hasFailedRows ? (
          <Button
            type="button"
            disabled={retrySubmitting || loading}
            isLoading={retrySubmitting}
            onClick={() => onRetryAll?.()}
          >
            Retry all
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          onClick={handleClose}
          disabled={retrySubmitting}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EncounterDetailModal;
